/**
 * «Βασικά» του προϊόντος: όλα τα πεδία του παλιού παραθύρου εκτός από μεγέθη, υλικά, ομάδες.
 * Οι κανόνες μένουν ίδιοι: τύπος ΥΠΗΡΕΣΙΑ → όχι ζυγιζόμενο· ζυγιζόμενο ↔ ελεύθερη τιμή αμοιβαία·
 * barcode ζυγιζόμενο → μονάδα kg + ζυγιζόμενο· «Κρατικά τέλη» μέσω utils/feeForm (Ε8α).
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Archive, ChevronDown, ChevronRight, Trash2 } from 'lucide-react';
import CatalogColorPicker from '../shared/CatalogColorPicker';
import { applyFeeMode, feeAmountText, feeModeBlocked, feeModeOf, FeeMode, FeeModeOptions, StateFeeRow } from '../../utils/feeForm';
import { CatRef, ProductForm, ensureSizeUids, VatCategory } from './catalogModel';
import type { ProductState } from './catalogDraft';
import { cx, Switch } from './ui';

export interface CategoryOption { ref: CatRef; label: string }

export interface BasicsTabProps {
  state: ProductState;
  changed: Set<string>;
  readOnly: boolean;
  isNew: boolean;
  feeOptions: FeeModeOptions;
  categoryOptions: CategoryOption[];
  vatCategories: VatCategory[];
  stateFees: Array<StateFeeRow & { short_label?: string }>;
  suppliers: Array<{ id: number; name: string }>;
  barcodeEnabled: boolean;
  kioskEnabled: boolean;
  warehouse: boolean;
  onChange: (fn: (s: ProductState) => ProductState) => void;
  onCategory: (ref: CatRef) => void;
  /** άμεσο, όπως ως σήμερα (και σε κεντρικό κατάλογο) — όχι με την Αποθήκευση */
  onKiosk: (next: boolean) => void;
  onWithdraw: () => void;
  onDiscard: () => void;
}

const ProductBasicsTab: React.FC<BasicsTabProps> = props => {
  const { t } = useTranslation(['catalog', 'products']);
  const { state, changed, readOnly, isNew } = props;
  const f = state.form;
  const setForm = (patch: Partial<ProductForm>) => props.onChange(s => ({ ...s, form: { ...s.form, ...patch } }));
  // Ανοιχτή η ενότητα που έχει ήδη τιμή, ώστε να μη μένει κρυφό ό,τι ισχύει.
  const [channelVatOpen, setChannelVatOpen] = useState(!!(f.vat_take_away || f.vat_delivery || f.vat_dine_in));
  const mark = (field: string) => (changed.has(field) ? 'cx-changed' : '');
  const dot = (field: string) => (changed.has(field) ? <span className="cx-edot" style={{ marginLeft: 6 }} title={t('catalog:list.unsaved')} /> : null);

  const setFeeMode = (mode: FeeMode, code: number | null = null) => {
    const result = applyFeeMode(f, mode, code, props.feeOptions);
    if (result.blocked) return; // το radio είναι ήδη κλειστό· φρουρός για κάθε άλλο μονοπάτι
    props.onChange(s => ({ ...s, form: { ...result.form, sizes: ensureSizeUids(result.form.sizes) } }));
  };

  const mode = feeModeOf(f);
  // Υπάρχον ΜΗ-τέλος με αποθηκευμένα μεγέθη δεν γίνεται τέλος (το τέλος δεν έχει μεγέθη).
  const feeBlocked = mode !== 'fee' && feeModeBlocked(props.feeOptions);
  const feeCode = f.fee_category ?? 8;
  const vatShort = t('catalog:fees.vatShort');
  const feeAmount = mode === 'fee' ? feeAmountText(props.stateFees, feeCode, vatShort) : null;
  const vatKnown = props.vatCategories.some(vc => Number(vc.rate) === Number(f.vat));
  const typeHint = f.product_type === 'ΠΡΟΙΟΝ' ? t('catalog:types.productHint') : f.product_type === 'ΥΠΗΡΕΣΙΑ' ? t('catalog:types.serviceHint') : t('catalog:types.goodsHint');
  const categoryKnown = props.categoryOptions.some(o => String(o.ref) === String(f.category_id));

  return (
    <div className="cx-form">
      <div className="cx-field">
        <label htmlFor="prod-name">{t('catalog:fields.name')}</label>
        <input id="prod-name" className={cx('cx-inp', mark('name'))} value={f.name} disabled={readOnly}
          placeholder={t('catalog:product.namePlaceholder')} onChange={e => setForm({ name: e.target.value })} />
      </div>
      <div className="cx-field">
        <label htmlFor="prod-desc">{t('catalog:fields.description')} <span className="cx-opt">{t('catalog:common.optional')}</span></label>
        <input id="prod-desc" className={cx('cx-inp', mark('description'))} value={f.description} disabled={readOnly}
          placeholder={t('catalog:product.descriptionPlaceholder')} onChange={e => setForm({ description: e.target.value })} />
      </div>

      <div className="cx-grid2">
        <div className="cx-field">
          <label htmlFor="prod-cat">{t('catalog:fields.category')}</label>
          <select id="prod-cat" className={cx('cx-inp', mark('category_id'))} disabled={readOnly} value={String(f.category_id)}
            onChange={e => {
              const raw = e.target.value;
              props.onCategory(/^\d+$/.test(raw) ? Number(raw) : raw);
            }}>
            {!categoryKnown && <option value={String(f.category_id)}>{t('catalog:product.unknownCategory', { id: f.category_id })}</option>}
            {props.categoryOptions.map(o => <option key={String(o.ref)} value={String(o.ref)}>{o.label}</option>)}
          </select>
        </div>
        <div className="cx-field">
          <label htmlFor="prod-type">{t('catalog:fields.type')}</label>
          <select id="prod-type" className={cx('cx-inp', mark('product_type'))} disabled={readOnly} value={f.product_type}
            onChange={e => {
              const type = e.target.value;
              setForm({ product_type: type, ...(type === 'ΥΠΗΡΕΣΙΑ' ? { is_weight_variable: false } : {}) });
            }}>
            <option value="ΕΜΠΟΡΕΥΜΑ">{t('catalog:types.goodsLong')}</option>
            <option value="ΠΡΟΙΟΝ">{t('catalog:types.productLong')}</option>
            <option value="ΥΠΗΡΕΣΙΑ">{t('catalog:types.serviceLong')}</option>
          </select>
        </div>
        <p className="cx-hint" style={{ gridColumn: '1 / -1', marginTop: -6 }}>{typeHint}</p>
        <div className="cx-field">
          <label htmlFor="prod-vat">{t('catalog:fields.vat')}</label>
          <select id="prod-vat" className={cx('cx-inp', mark('vat'))} disabled={readOnly} value={String(f.vat)}
            onChange={e => setForm({ vat: parseFloat(e.target.value) || 0 })}>
            {/* Τιμή που δεν υπάρχει στη λίστα (παλιά δεδομένα) φαίνεται όπως είναι. */}
            {(!vatKnown || props.vatCategories.length === 0) && <option value={String(f.vat)}>{f.vat}%</option>}
            {props.vatCategories.map(vc => (
              <option key={vc.id} value={String(Number(vc.rate))}>{Number(vc.rate)}%{vc.name ? ` (${vc.name})` : ''}</option>
            ))}
          </select>
        </div>
        <div className="cx-field">
          <label htmlFor="prod-max-discount">{t('catalog:fields.maxDiscount')}</label>
          <div className="cx-sfx">
            <input id="prod-max-discount" type="number" min={0} max={100} step={1} className={cx('cx-inp num', mark('max_discount_percent'))}
              disabled={readOnly} value={f.max_discount_percent ?? 100}
              onChange={e => {
                const v = e.target.value === '' ? 100 : parseInt(e.target.value, 10);
                setForm({ max_discount_percent: Math.max(0, Math.min(100, Number.isNaN(v) ? 100 : v)) });
              }} />
            <span>%</span>
          </div>
        </div>
        <p className="cx-hint" style={{ gridColumn: '1 / -1', marginTop: -6 }}>{t('catalog:product.maxDiscountHint')}</p>
      </div>

      <div className={cx('cx-field', changed.has('color') && 'cx-field-changed')}>
        {dot('color')}
        <CatalogColorPicker kind="product" idPrefix="prod-color" value={f.color} onChange={tone => !readOnly && setForm({ color: tone })} />
      </div>

      <fieldset className="cx-field" style={{ border: 0, padding: 0, margin: 0 }} disabled={readOnly}>
        <legend className="cx-lbl" style={{ marginBottom: 6 }}>
          {t('catalog:fields.fees')} <span className="cx-opt">{t('catalog:fees.onlyTakeawayDelivery')}</span>{dot('fee')}
        </legend>
        <div className="cx-radios">
          <label className="cx-rad">
            <input type="radio" name="fee-mode" checked={mode === 'none'} onChange={() => setFeeMode('none')} />
            {t('catalog:fees.none')}
          </label>
          <label className={cx('cx-rad', feeBlocked && 'blocked')}>
            {/* ΦΠΑ 24 και έκπτωση 0 (τα τέλη περνούν αυτούσια)· το ποσό είναι του state_fees. */}
            <input type="radio" name="fee-mode" checked={mode === 'fee'} disabled={feeBlocked} onChange={() => setFeeMode('fee', 8)} />
            {t('catalog:fees.isFee')}
            {mode === 'fee' && (
              <select className="cx-inp sm" aria-label={t('catalog:fees.code')} value={feeCode} onChange={e => setFeeMode('fee', parseInt(e.target.value, 10))}>
                {props.stateFees.length === 0 ? (
                  <>
                    <option value={8}>{t('catalog:fees.code8')}</option>
                    <option value={16}>{t('catalog:fees.code16')}</option>
                  </>
                ) : props.stateFees.map(fee => {
                  const amount = feeAmountText(props.stateFees, fee.code, vatShort);
                  return <option key={fee.code} value={fee.code}>{t('catalog:fees.codeOption', { code: fee.code, label: fee.short_label || fee.title || '' })}{amount ? ` (${amount})` : ''}</option>;
                })}
              </select>
            )}
            {mode === 'fee' && feeAmount && <span className="cx-hint" style={{ flexBasis: '100%' }}>{t('catalog:fees.amountInfo', { amount: feeAmount })}</span>}
            {feeBlocked && <span className="cx-hint warn" style={{ flexBasis: '100%' }}>{t('catalog:fees.requiresNoSizes')}</span>}
          </label>
          <label className="cx-rad">
            <input type="radio" name="fee-mode" checked={mode === 'trigger'} onChange={() => setFeeMode('trigger')} />
            {t('catalog:fees.trigger')}
            {mode === 'trigger' && (
              <span className="cx-sfx">
                <input type="number" min={1} step={1} className="cx-inp sm num" aria-label={t('catalog:fees.count')}
                  value={f.auto_plastic_fee_count ?? 1}
                  onChange={e => {
                    const v = parseInt(e.target.value, 10);
                    setForm({ auto_plastic_fee_count: Number.isFinite(v) && v > 0 ? v : 1, fee_category: null });
                  }} />
                <span>{t('catalog:fees.pieces')}</span>
              </span>
            )}
          </label>
        </div>
      </fieldset>

      {f.product_type !== 'ΥΠΗΡΕΣΙΑ' && (
        <div className="cx-sw-row">
          {/* Ζυγιζόμενο ΚΑΙ ελεύθερη τιμή επιτρέπονται μαζί (19/10/2026, «Βάρος + τιμή κιλού»): στο
              ταμείο πρώτα το βάρος, μετά η τιμή κιλού (utils/weightedOpenPrice). */}
          <Switch checked={f.is_weight_variable} disabled={readOnly} label={t('catalog:fields.weighted')} pending={changed.has('is_weight_variable')}
            onChange={next => setForm({ is_weight_variable: next })} />
          <span><b>{t('catalog:fields.weighted')}</b><span className="cx-hint">{t('catalog:product.weightedHint')}</span></span>
        </div>
      )}
      <div className="cx-sw-row">
        <Switch checked={f.is_open_price} disabled={readOnly} label={t('catalog:fields.openPrice')} pending={changed.has('is_open_price')}
          onChange={next => setForm({ is_open_price: next })} />
        <span>
          <b>{t('catalog:fields.openPrice')}</b>
          <span className="cx-hint">
            {f.is_open_price && f.is_weight_variable ? t('catalog:product.weightedOpenPriceHint') : t('catalog:product.openPriceHint')}
          </span>
        </span>
      </div>
      {props.kioskEnabled && (
        <div className="cx-sw-row">
          <Switch checked={state.is_kiosk_recommended} label={t('catalog:fields.kiosk')} pending={changed.has('is_kiosk_recommended')}
            onChange={props.onKiosk} />
          <span><b>{t('catalog:fields.kiosk')}</b><span className="cx-hint">{isNew ? t('catalog:product.kioskHint') : t('catalog:product.kioskInstantHint')}</span></span>
        </div>
      )}

      {props.barcodeEnabled && f.product_type !== 'ΥΠΗΡΕΣΙΑ' && (
        <div className="cx-box">
          <div className="cx-sec-title sm">{t('catalog:fields.barcode')}</div>
          <div className="cx-grid2">
            <div className="cx-field">
              <label htmlFor="prod-barcode">{t('catalog:barcode.code')}</label>
              <input id="prod-barcode" className={cx('cx-inp', mark('barcode'))} disabled={readOnly} value={f.barcode}
                placeholder={f.barcode_type === 'weighted' ? 'PLU (5)' : 'EAN-13, Code128…'} onChange={e => setForm({ barcode: e.target.value })} />
            </div>
            <div className="cx-field">
              <label htmlFor="prod-barcode-type">{t('catalog:fields.barcodeType')}</label>
              <select id="prod-barcode-type" className={cx('cx-inp', mark('barcode_type'))} disabled={readOnly} value={f.barcode_type}
                onChange={e => {
                  const type = e.target.value as 'standard' | 'weighted';
                  setForm({ barcode_type: type, sell_unit: type === 'weighted' ? 'kg' : 'piece', is_weight_variable: type === 'weighted' ? true : f.is_weight_variable });
                }}>
                <option value="standard">{t('catalog:barcode.standard')}</option>
                <option value="weighted">{t('catalog:barcode.weightedLong')}</option>
              </select>
            </div>
            {f.barcode_type === 'weighted' && (
              <div className="cx-field">
                <label htmlFor="prod-sell-unit">{t('catalog:fields.sellUnit')}</label>
                <select id="prod-sell-unit" className={cx('cx-inp', mark('sell_unit'))} disabled={readOnly} value={f.sell_unit}
                  onChange={e => setForm({ sell_unit: e.target.value as ProductForm['sell_unit'] })}>
                  <option value="kg">{t('catalog:units.kgLong')}</option>
                  <option value="g">{t('catalog:units.gLong')}</option>
                </select>
              </div>
            )}
            {/* Ε4α: η τιμή μονάδας δεν έχει δικό της πεδίο — είναι η τιμή του μεγέθους. */}
            {f.barcode_type === 'weighted' && <p className="cx-hint" style={{ alignSelf: 'end' }}>{t('catalog:barcode.weightedPriceInSizes')}</p>}
          </div>
          <p className="cx-hint">{f.barcode_type === 'weighted' ? t('catalog:barcode.weightedHint') : t('catalog:barcode.standardHint')}</p>
        </div>
      )}

      {/* ΦΠΑ καναλιού: ανήκει στο προϊόν σε κάθε μοντέλο τιμής· κενό = ο βασικός ΦΠΑ. */}
      <div>
        <button type="button" className="cx-disclosure" aria-expanded={channelVatOpen} onClick={() => setChannelVatOpen(o => !o)}>
          {channelVatOpen ? <ChevronDown className="cx-ico" /> : <ChevronRight className="cx-ico" />}
          {t('catalog:product.vatByOrderType')} <span className="cx-opt">{t('catalog:common.optional')}</span>
          {(changed.has('vat_take_away') || changed.has('vat_delivery') || changed.has('vat_dine_in')) && <span className="cx-edot" style={{ marginLeft: 'auto' }} />}
        </button>
        {channelVatOpen && (
          <>
            <div className="cx-grid3" style={{ marginTop: 10 }}>
              {([
                { id: 'vat-takeaway', label: t('catalog:sizes.takeAway'), field: 'vat_take_away' },
                { id: 'vat-delivery', label: t('catalog:sizes.delivery'), field: 'vat_delivery' },
                { id: 'vat-dinein', label: t('catalog:sizes.dineIn'), field: 'vat_dine_in' },
              ] as const).map(ch => (
                <div key={ch.id} className="cx-field">
                  <label htmlFor={ch.id}>{ch.label}</label>
                  <div className="cx-sfx">
                    <input id={ch.id} type="number" step="0.01" className={cx('cx-inp num', mark(ch.field))} disabled={readOnly}
                      placeholder={String(f.vat ?? '')} value={f[ch.field] || ''}
                      onChange={e => setForm({ [ch.field]: e.target.value ? parseFloat(e.target.value) : undefined } as Partial<ProductForm>)} />
                    <span>%</span>
                  </div>
                </div>
              ))}
            </div>
            {/* Ο ΦΠΑ take away/delivery αποθηκεύεται αλλά δεν τον διαβάζει καμία ροή απόδειξης (μόνο το dine-in). */}
            <p className="cx-hint" style={{ marginTop: 8 }}>{t('catalog:product.vatByOrderTypeHint')}</p>
          </>
        )}
      </div>

      {f.product_type === 'ΕΜΠΟΡΕΥΜΑ' && props.warehouse && (
        <div className="cx-box">
          <div className="cx-sec-title sm">{t('catalog:merch.title')}</div>
          <div className="cx-field">
            <label htmlFor="supplier">{t('catalog:fields.supplier')}</label>
            <select id="supplier" className={cx('cx-inp', mark('supplier_id'))} disabled={readOnly} value={f.supplier_id ?? ''}
              onChange={e => setForm({ supplier_id: parseInt(e.target.value, 10) || undefined })}>
              <option value="">{t('catalog:merch.selectSupplier')}</option>
              {props.suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div className="cx-grid2">
            <div className="cx-field">
              <label htmlFor="min-stock">{t('catalog:fields.minStock')}</label>
              <input id="min-stock" type="number" className={cx('cx-inp num', mark('min_stock_level'))} disabled={readOnly} placeholder="10"
                value={f.min_stock_level || ''} onChange={e => setForm({ min_stock_level: parseInt(e.target.value, 10) || undefined })} />
            </div>
            <div className="cx-field">
              <label htmlFor="current-stock">{t('catalog:fields.currentStock')}</label>
              <input id="current-stock" type="number" className={cx('cx-inp num', mark('current_stock'))} disabled={readOnly} placeholder="0"
                value={f.current_stock || ''} onChange={e => setForm({ current_stock: parseInt(e.target.value, 10) || undefined })} />
            </div>
          </div>
          <label className="cx-cb-row">
            <input id="can-be-ingredient" type="checkbox" className="cx-cb" disabled={readOnly} checked={f.can_be_ingredient || false}
              onChange={e => setForm({ can_be_ingredient: e.target.checked })} />
            {t('catalog:fields.canBeIngredient')}{dot('can_be_ingredient')}
          </label>
          {f.can_be_ingredient && (
            <>
              <div className="cx-grid2">
                <div className="cx-field">
                  <label htmlFor="stock-unit">{t('catalog:fields.stockUnit')}</label>
                  <select id="stock-unit" className={cx('cx-inp', mark('stock_unit'))} disabled={readOnly} value={f.stock_unit || ''}
                    onChange={e => setForm({ stock_unit: e.target.value })}>
                    <option value="">{t('catalog:merch.selectUnit')}</option>
                    {['ml', 'l', 'g', 'kg', 'piece'].map(u => <option key={u} value={u}>{t(`catalog:stockUnits.${u}`)}</option>)}
                  </select>
                </div>
                <div className="cx-field">
                  <label htmlFor="stock-unit-quantity">{t('catalog:fields.stockUnitQuantity')} <span className="cx-opt">{t('catalog:merch.quantityHint')}</span></label>
                  <input id="stock-unit-quantity" type="number" step="0.01" className={cx('cx-inp num', mark('stock_unit_quantity'))} disabled={readOnly}
                    placeholder="330" value={f.stock_unit_quantity || ''} onChange={e => setForm({ stock_unit_quantity: parseFloat(e.target.value) || undefined })} />
                </div>
              </div>
              <p className="cx-hint">{t('catalog:merch.example')}</p>
            </>
          )}
        </div>
      )}

      {!readOnly && (
        <div className="cx-danger-zone">
          {isNew ? (
            <>
              <button type="button" className="cx-btn sm danger" onClick={props.onDiscard}><Trash2 className="cx-ico" /> {t('catalog:product.discardNew')}</button>
              <span className="cx-hint">{t('catalog:product.discardNewHint')}</span>
            </>
          ) : (
            <>
              <button type="button" className="cx-btn sm danger" onClick={props.onWithdraw}><Archive className="cx-ico" /> {t('catalog:withdraw.button')}</button>
              <span className="cx-hint">{t('catalog:withdraw.hint')}</span>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductBasicsTab;
